import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Plus, Lock, GitBranch, MoreHorizontal, CalendarDays } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { PageHeader, StatusBadge, Loading, DemoBadge } from "@/components/common";

const COLS = [["bloqueada", "Bloqueadas"], ["pendiente", "Pendientes"], ["en_proceso", "En proceso"], ["completada", "Completadas"]];

export default function Tasks() {
  const tasks = useFetch("/intranet/tasks", { poll: 10000 });
  const cats = useFetch("/meta/catalogs");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", dependency: "obras", depends_on: [], due_date: "", priority: "media" });
  const deps = cats.data?.dependencies || {};
  const byId = Object.fromEntries((tasks.data || []).map((t) => [t.task_id, t]));

  const move = async (t, status) => {
    try { await api.patch(`/intranet/tasks/${t.task_id}`, { status }); toast.success("Tarea actualizada"); tasks.reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const create = async () => {
    try {
      await api.post("/intranet/tasks", { ...f, due_date: f.due_date || null });
      toast.success("Tarea creada"); setOpen(false); setF({ title: "", dependency: "obras", depends_on: [], due_date: "", priority: "media" }); tasks.reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Intranet" title="Tareas interdependencias" subtitle="Coordina obras entre dependencias: una tarea solo avanza cuando sus predecesoras están completadas."
        actions={<Button onClick={() => setOpen(true)} data-testid="tasks-create-button"><Plus className="h-4 w-4 mr-1" />Nueva tarea</Button>} />
      {tasks.loading ? <Loading /> : (
        <ScrollArea className="w-full">
          <div className="flex gap-4 pb-4">
            {COLS.map(([key, label]) => {
              const items = (tasks.data || []).filter((t) => t.status === key);
              return (
                <div key={key} className="min-w-[290px] flex-1 rounded-xl bg-secondary p-3" data-testid={`tasks-column-${key}`}>
                  <div className="flex items-center justify-between mb-3 px-1"><span className="text-sm font-semibold">{label}</span><span className="text-xs text-muted-foreground">{items.length}</span></div>
                  <div className="space-y-2">
                    {items.map((t) => (
                      <Card key={t.task_id} className="bg-white card-hover" data-testid={`task-card-${t.task_id}`}>
                        <CardContent className="p-3 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-sm font-medium leading-snug">{t.title}</div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><button className="p-1 rounded hover:bg-secondary" data-testid={`task-menu-${t.task_id}`}><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-white">
                                {[["pendiente", "Mover a pendiente"], ["en_proceso", "Iniciar"], ["completada", "Completar"]].filter(([s]) => s !== t.status).map(([s, l]) => (
                                  <DropdownMenuItem key={s} onClick={() => move(t, s)} data-testid={`task-move-${s}-${t.task_id}`}>{l}</DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-[11px] rounded bg-[#DFF3F6] text-[#006A7C] px-1.5 py-0.5">{deps[t.dependency]}</span>
                            <StatusBadge status={t.priority} />
                            {t.is_demo && <DemoBadge />}
                          </div>
                          {t.depends_on?.length > 0 && (
                            <div className="space-y-1">
                              {t.depends_on.map((d) => (
                                <div key={d} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                  {byId[d]?.status === "completada" ? <GitBranch className="h-3 w-3 text-emerald-600" /> : <Lock className="h-3 w-3 text-rose-600" />}
                                  <span className="truncate">{byId[d]?.title || d}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {t.due_date && <div className="text-[11px] text-muted-foreground flex items-center gap-1"><CalendarDays className="h-3 w-3" />{t.due_date}</div>}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva tarea</DialogTitle><DialogDescription>Define predecesoras de otras dependencias para coordinar el orden de ejecución.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Título</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Dependencia responsable</Label>
                <Select value={f.dependency} onValueChange={(v) => setF({ ...f, dependency: v })}><SelectTrigger data-testid="task-dependency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(deps).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Fecha límite</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-due-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Depende de</Label>
              <div className="max-h-44 overflow-y-auto rounded-lg border p-2 space-y-1.5">
                {(tasks.data || []).map((t) => (
                  <label key={t.task_id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={f.depends_on.includes(t.task_id)} data-testid={`task-dep-${t.task_id}`}
                      onCheckedChange={(c) => setF({ ...f, depends_on: c ? [...f.depends_on, t.task_id] : f.depends_on.filter((x) => x !== t.task_id) })} />
                    <span className="truncate">{t.title}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button className="w-full" disabled={f.title.length < 3} onClick={create} data-testid="task-submit-button">Crear tarea</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
